// src/components/product/ProductsTable.tsx
//
// ProductsTable
// - Renders list controls via StandardTableToolbar + StandardPagination.
// - Uses StandardTableFrame for loading/empty states.
// - Navigation policy: product code opens the detail page.
// - Row status is derived from productStatus (warning/inactive/normal).

import React, { useCallback, useMemo } from 'react';
import { Box, Button, IconButton, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import {
  CheckCircleOutline,
  ContentCopy,
  PauseCircleOutline,
  ReportProblemOutlined,
} from '@mui/icons-material';

import { ProductStatus } from '@models/product';
import { getProductStatusMeta } from '@utils/productLabels';

import {
  StandardTableToolbar,
  StandardPagination,
  StandardTableFrame,
  TableRowStatus,
  cellSx,
} from '@components/common/table';
import { useListReturnNavigation } from '@hooks/useListReturnNavigation';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';
import { ProductSaleTypeDisplay } from '@components/product/ProductSaleTypeDisplay';
import { ProductTypeDisplay } from '@components/product/PartnerProductSpecDisplays';

// TODO: Replace with real ProductResponse when available.
type ProductRow = any;

const iconSx = {
  fontSize: 16,
  flex: '0 0 auto',
} as const;

const formatNumber = (value: unknown) => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString();
};

const formatUsdAmount = (value: unknown) => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getProductStatusDisplay = (status?: ProductStatus | string | null) => {
  const meta = getProductStatusMeta(status);

  switch (status) {
    case ProductStatus.OUT_OF_STOCK:
      return {
        label: meta.label,
        color: 'warning.main',
        icon: <ReportProblemOutlined sx={iconSx} />,
      };
    case ProductStatus.DISCONTINUED:
      return {
        label: meta.label,
        color: 'text.disabled',
        icon: <PauseCircleOutline sx={iconSx} />,
      };
    case ProductStatus.ACTIVE:
    default:
      return {
        label: meta.label,
        color: 'success.main',
        icon: <CheckCircleOutline sx={iconSx} />,
      };
  }
};

const getCountSx = (value: number, kind?: 'refunded') => {
  const base = {
    ...cellSx.number,
    color: value <= 0 ? 'text.disabled' : 'text.primary',
    fontWeight: value <= 0 ? 500 : 700,
  };

  if (kind === 'refunded' && value > 0) {
    return {
      ...base,
      color: 'warning.main',
      fontWeight: 800,
    };
  }

  return base;
};

interface ProductsTableProps {
  data: ProductRow[];
  isLoading: boolean;
  totalCount?: number;
  page: number;
  pageSize: number;

  /**
   * Page change handler (page number only).
   * Keeping this simple avoids passing synthetic events across layers.
   */
  onChangePage: (nextPage: number) => void;
}

const ProductsTable = ({ data, isLoading, totalCount = 0, page, pageSize, onChangePage }: ProductsTableProps) => {
  const { navigateFromList } = useListReturnNavigation();
  const systemAlert = useSystemAlert();
  const rows = useMemo(() => data ?? [], [data]);

  const handleOpenDetail = useCallback(
    (productId: number) => {
      if (!Number.isFinite(productId) || productId <= 0) return;
      navigateFromList(`/products/${productId}`);
    },
    [navigateFromList],
  );

  const getRowStatus = useCallback((row: ProductRow): TableRowStatus => {
    const statusValue = String(row.productStatus ?? '');
    if (statusValue === ProductStatus.OUT_OF_STOCK) return 'warning';
    if (statusValue === ProductStatus.DISCONTINUED) return 'inactive';
    return 'normal';
  }, []);

  const handleCopyProductCode = useCallback(
    async (productCode: unknown) => {
      const text = String(productCode ?? '').trim();
      if (!text) return;

      try {
        await navigator.clipboard.writeText(text);
        systemAlert.success('Product code copied.', { durationMs: 1600 });
      } catch {
        systemAlert.error('Copy failed.');
      }
    },
    [systemAlert],
  );

  // (# + Product Code + Product Name + Product Type + Stock Code + Order Type + Price + Shipped(7d) + Shipped(total) + Refunded)
  const columnsCount = 10;

  return (
    <Box>
      <StandardTableToolbar
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onChangePage={onChangePage}
        showTotalCount
        showPageJump
      />

      <Box mt={1}>
        <StandardTableFrame<ProductRow>
          minWidth={1375}
          columnsCount={columnsCount}
          isLoading={isLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No products found."
          rows={rows}
          getRowKey={(row) => String(row.productId)}
          getRowStatus={getRowStatus}
          head={
            <TableRow>
              <TableCell width={70} align="right">
                #
              </TableCell>
              <TableCell sx={{ minWidth: 190 }}>Product Code</TableCell>
              <TableCell sx={{ minWidth: 260 }}>Product Name</TableCell>
              <TableCell width={125} sx={cellSx.nowrap}>
                Product Type
              </TableCell>
              <TableCell width={140} sx={cellSx.nowrap}>
                Stock Code
              </TableCell>
              <TableCell width={140} sx={cellSx.nowrap}>
                Order Type
              </TableCell>
              <TableCell width={120} align="right" sx={cellSx.nowrap}>
                Price (USD)
              </TableCell>
              <TableCell width={140} align="right" sx={cellSx.nowrap}>
                Shipped (7d)
              </TableCell>
              <TableCell width={150} align="right" sx={cellSx.nowrap}>
                Shipped (Total)
              </TableCell>
              <TableCell width={120} align="right" sx={cellSx.nowrap}>
                Refunded
              </TableCell>
            </TableRow>
          }
          renderRow={(row, index) => {
            const productId = Number(row.productId);
            const canNavigate = Number.isFinite(productId) && productId > 0;
            const statusDisplay = getProductStatusDisplay(row.productStatus);
            const shippedWeek = Number(row.lastWeekShippedCount ?? 0);
            const shippedTotal = Number(row.totalShippedCount ?? 0);
            const refunded = Number(row.refundedCount ?? 0);
            const isInactive = String(row.productStatus ?? '') === ProductStatus.DISCONTINUED;
            const productCode = String(row.productCode ?? '').trim();
            const canCopyProductCode = Boolean(productCode);

            return (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  {(page - 1) * pageSize + index + 1}
                </TableCell>

                <TableCell sx={{ ...cellSx.nowrap, opacity: isInactive ? 0.68 : 1 }}>
                  <Stack direction="row" spacing={0.9} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        color: statusDisplay.color,
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}
                      aria-label={statusDisplay.label}
                      title={statusDisplay.label}
                    >
                      {statusDisplay.icon}
                    </Box>
                    {canNavigate ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleOpenDetail(productId)}
                        sx={{
                          minWidth: 0,
                          px: 0,
                          fontWeight: 800,
                          textTransform: 'none',
                          textDecoration: 'underline',
                          textUnderlineOffset: '3px',
                        }}
                      >
                        {row.productCode || '-'}
                      </Button>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {row.productCode || '-'}
                      </Typography>
                    )}
                    {canCopyProductCode && (
                      <Tooltip title="Copy product code" arrow>
                        <IconButton
                          size="small"
                          aria-label="Copy product code"
                          onClick={() => handleCopyProductCode(productCode)}
                          sx={{
                            width: 24,
                            height: 24,
                            color: 'text.secondary',
                            opacity: 0.9,
                            flexShrink: 0,
                            '&:hover': {
                              opacity: 1,
                              color: 'primary.main',
                            },
                          }}
                        >
                          <ContentCopy sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>

                <TableCell sx={{ minWidth: 260, opacity: isInactive ? 0.68 : 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                    {row.productName || '-'}
                  </Typography>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <ProductTypeDisplay productType={row.productType} />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: row.stock?.stockCode ? 'text.primary' : 'text.disabled',
                      fontWeight: row.stock?.stockCode ? 700 : 500,
                      fontSize: 12,
                      letterSpacing: 0,
                    }}
                  >
                    {row.stock?.stockCode ?? '-'}
                  </Typography>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <ProductSaleTypeDisplay saleType={row.productSaleType} />
                </TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {formatUsdAmount(row.price)}
                </TableCell>

                <TableCell align="right" sx={getCountSx(shippedWeek)}>
                  {formatNumber(shippedWeek)}
                </TableCell>

                <TableCell align="right" sx={getCountSx(shippedTotal)}>
                  {formatNumber(shippedTotal)}
                </TableCell>

                <TableCell align="right" sx={getCountSx(refunded, 'refunded')}>
                  {formatNumber(refunded)}
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onChangePage={onChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default ProductsTable;
